import apiClient from './client'

export interface ChatAction {
  type: 'navigate_athletes'
  filter_ids?: string[]
  filter_label?: string
}

// Long Polling: response when enqueueing message
export interface EnqueueMessageResponse {
  user_message_id: string
  assistant_message_id: string
  status: 'pending'
}

export interface ChatHistoryMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  action?: ChatAction
  status: 'pending' | 'processing' | 'completed' | 'failed'
  created_at: string
}

export interface AIUsageInfo {
  used: number
  limit: number
  remaining: number
}

export const chatApi = {
  /**
   * POST /api/v1/assistants/{assistantId}/chat
   * Enqueues a chat message for async processing (Long Polling).
   * Returns 202 Accepted with message_id.
   */
  enqueueMessage: async (assistantId: string, message: string): Promise<EnqueueMessageResponse> => {
    const response = await apiClient.post<EnqueueMessageResponse>(
      `/assistants/${assistantId}/chat`,
      { message }
    )
    return response.data
  },

  /**
   * GET /api/v1/assistants/{assistantId}/chat/poll?after=timestamp
   * Long Polling endpoint - waits up to 25 seconds for new messages.
   * Returns new messages or empty array on timeout.
   */
  pollForNewMessages: async (
    assistantId: string,
    afterTimestamp: number,
    signal?: AbortSignal
  ): Promise<ChatHistoryMessage[]> => {
    const response = await apiClient.get<ChatHistoryMessage[]>(
      `/assistants/${assistantId}/chat/poll`,
      {
        params: { after: afterTimestamp },
        timeout: 30000, // 30 seconds (slightly longer than server timeout)
        signal,
      }
    )
    return response.data
  },

  /**
   * GET /api/v1/assistants/{assistantId}/chat/history
   * Returns chat history (user + assistant messages only).
   */
  getHistory: async (assistantId: string, limit = 50): Promise<ChatHistoryMessage[]> => {
    const response = await apiClient.get<ChatHistoryMessage[]>(
      `/assistants/${assistantId}/chat/history`,
      { params: { limit } }
    )
    return response.data
  },

  /**
   * GET /api/v1/users/me/ai-usage
   * Returns current month AI usage info.
   */
  getAIUsage: async (): Promise<AIUsageInfo> => {
    const response = await apiClient.get<AIUsageInfo>('/users/me/ai-usage')
    return response.data
  },
}
